/**
 * Cấu hình tab Sales Control — bám 10 nav button trên `form_frmCtrlSalesMain.txt`
 * (`navCtrlSales*` trong `NavigationControl0`). Mỗi tab có evidence subform riêng
 * trong `frontend/plan/access_all/form_frmCtrlSalesMainSubForm*.txt`.
 */

export type SaleControlTabKey =
  | 'sales-contract'
  | 'sales-allocation'
  | 'delivery-order'
  | 'invoice'
  | 'receive-order'
  | 'claim'
  | 'credit-note'
  | 'debit-note'
  | 'moving'
  | 'references'

export interface SaleControlTabConfig {
  /** Web tab key — segment trong `/sales/control/:key`. */
  key: SaleControlTabKey
  /** Label hiển thị trên Tabs (web product naming). */
  label: string
  /** Caption gốc trong Access `navCtrlSales*`. */
  accessLabel: string
  /** `NavigationTargetName` từ `form_frmCtrlSalesMain.txt`. */
  accessForm: string
  /** `SourceObject` mà VBA `navCtrl*_Click` đặt vào `SALECS_SCREEN_NAME`. */
  accessQueryForm?: string
  /** Danh sách evidence (form/query/report) cần đọc trước khi build chi tiết. */
  evidence: string[]
  /** Action buttons toolbar trên subform — mới chỉ liệt kê, chưa wire CRUD/print. */
  actions: string[]
  description: string
}

export const SALE_CONTROL_TAB_CONFIG: SaleControlTabConfig[] = [
  {
    key: 'sales-contract',
    label: 'Sales Contract',
    accessLabel: 'Sales Contract',
    accessForm: 'frmCtrlSalesMainSubFormLocalS',
    accessQueryForm: 'frmCtrlSalesQryLocalS',
    evidence: [
      'form_frmCtrlSalesMainSubFormLocalS.txt',
      'form_frmCtrlSalesQryLocalS.txt',
      'query_CtrlSalesQryLocalS.txt',
      'form_frmCtrlSalesQryLocalS_CloseAdd.txt',
    ],
    actions: ['Forced close', 'Re-open', 'Filling Docs'],
    description:
      'Sales control list for local sales contracts. Default landing tab — `public_mv_local_s`.',
  },
  {
    key: 'sales-allocation',
    label: 'Sales Allocation',
    accessLabel: 'Sales Allocation',
    accessForm: 'frmCtrlSalesMainSubFormSaleAlc',
    accessQueryForm: 'frmCtrlSalesQrySaleAlc',
    evidence: [
      'form_frmCtrlSalesMainSubFormSaleAlc.txt',
      'form_frmCtrlSalesQrySaleAlc.txt',
      'query_CtrlSalesQrySalesAlc.txt',
    ],
    actions: ['New', 'Edit', 'Delete', 'Copy', 'Mass Upload', 'Edit Booking', 'Edit similar DO', 'Print - BBGN', 'Print - VGM'],
    description: 'Allocation list and helper actions for sales allocation workflows.',
  },
  {
    key: 'delivery-order',
    label: 'Delivery Order',
    accessLabel: 'Delivery Order',
    accessForm: 'frmCtrlSalesMainSubFormDO',
    accessQueryForm: 'frmCtrlSalesQryDO',
    evidence: [
      'form_frmCtrlSalesMainSubFormDO.txt',
      'form_frmCtrlSalesQryDO.txt',
      'query_CtrlSalesQryDO.txt',
    ],
    actions: ['New', 'Edit', 'Delete', 'Copy', 'Print - SA', 'Print - DO', 'Print - Dispatch', 'Print - SA (BL No)'],
    description: 'Delivery-order tab with SA/DO/dispatch print workflows.',
  },
  {
    key: 'invoice',
    label: 'Invoice',
    accessLabel: 'Invoice',
    accessForm: 'frmCtrlSalesMainSubFormInvoice',
    accessQueryForm: 'frmCtrlSalesQryInvoice',
    evidence: [
      'form_frmCtrlSalesMainSubFormInvoice.txt',
      'form_frmCtrlSalesQryInvoice.txt',
      'query_CtrlSalesQryInvoice.txt',
    ],
    actions: ['New', 'Edit', 'Delete', 'Copy', 'Add DO', 'Receivables Check', 'Print - Customer', 'Print - Customs', 'Print - PKL', 'Print - Dispatch'],
    description: 'Invoice tab with customer/customs/PKL/dispatch print variants.',
  },
  {
    key: 'receive-order',
    label: 'Receive Order',
    accessLabel: 'Release Order',
    accessForm: 'frmCtrlSalesMainSubformRO',
    accessQueryForm: 'frmCtrlSalesQryRO',
    evidence: [
      'form_frmCtrlSalesMainSubformRO.txt',
      'form_frmCtrlSalesQryRO.txt',
      'query_CtrlSalesQryRO.txt',
    ],
    actions: ['New', 'Edit', 'Delete', 'Print', 'Release Details'],
    description: 'Release-order workflow surfaced with the current web label Receive Order.',
  },
  {
    key: 'claim',
    label: 'Claim',
    accessLabel: 'Claims',
    accessForm: 'frmCtrlSalesMainSubFormClaim',
    accessQueryForm: 'frmCtrlSalesQryClaim',
    evidence: [
      'form_frmCtrlSalesMainSubFormClaim.txt',
      'form_frmCtrlSalesQryClaim.txt',
      'query_CtrlSalesQryClaim.txt',
    ],
    actions: ['New', 'Edit', 'Delete', 'Print'],
    description: 'Claims tab following Access field order and print evidence.',
  },
  {
    key: 'credit-note',
    label: 'Credit Note',
    accessLabel: 'Credit Note',
    accessForm: 'frmCtrlSalesMainSubFormCredit',
    accessQueryForm: 'frmCtrlSalesQryCredit',
    evidence: [
      'form_frmCtrlSalesMainSubFormCredit.txt',
      'form_frmCtrlSalesQryCredit.txt',
      'query_CtrlSalesQryCredit.txt',
    ],
    actions: ['New', 'Edit', 'Delete', 'Print'],
    description: 'Credit note tab with its own query/report mapping.',
  },
  {
    key: 'debit-note',
    label: 'Debit Note',
    accessLabel: 'Debit Note',
    accessForm: 'frmCtrlSalesMainSubFormDebit',
    accessQueryForm: 'frmCtrlSalesQryDebit',
    evidence: [
      'form_frmCtrlSalesMainSubFormDebit.txt',
      'form_frmCtrlSalesQryDebit.txt',
      'query_CtrlSalesQryDebit.txt',
    ],
    actions: ['New', 'Edit', 'Delete', 'Print'],
    description: 'Debit note tab with separate print and edit flow.',
  },
  {
    key: 'moving',
    label: 'Moving',
    accessLabel: 'Internal Moving',
    accessForm: 'frmCtrlSalesMainSubFormMoving',
    accessQueryForm: 'frmCtrlSalesQryMoving',
    evidence: [
      'form_frmCtrlSalesMainSubFormMoving.txt',
      'form_frmCtrlSalesQryMoving.txt',
      'query_CtrlSalesQryMoving.txt',
    ],
    actions: ['New', 'Edit', 'Delete', 'Print'],
    description: 'Internal moving list supports multi-record (1–5) print behavior.',
  },
  {
    key: 'references',
    label: 'References',
    accessLabel: 'References',
    accessForm: 'frmCtrlSalesMainSubFormReferences',
    evidence: [
      'form_frmCtrlSalesMainSubFormReferences.txt',
      'form_frmCtrlSalesQryRefCP.txt',
      'form_frmCtrlSalesQryRefCertification.txt',
    ],
    actions: ['Go to Page selector'],
    description: 'Reference hub for the Sales Control module.',
  },
]

export function isSaleControlTabKey(value: unknown): value is SaleControlTabKey {
  return typeof value === 'string' && SALE_CONTROL_TAB_CONFIG.some((tab) => tab.key === value)
}

export function getSaleControlTab(key: SaleControlTabKey): SaleControlTabConfig {
  return SALE_CONTROL_TAB_CONFIG.find((tab) => tab.key === key) ?? SALE_CONTROL_TAB_CONFIG[0]
}
